import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import api, { publicTenantLogoUrl } from "@/lib/api";
import {
  ShoppingBag, Newspaper, Briefcase, MessageSquare, Home,
  Sparkles, User, LogIn, UserPlus, LogOut, Package,
} from "lucide-react";
import { CustomerAuthProvider, useCustomerAuth } from "@/contexts/CustomerAuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/site", label: "Home", icon: Home, exact: true },
  { to: "/site/shop", label: "Shop", icon: ShoppingBag },
  { to: "/site/news", label: "News", icon: Newspaper },
  { to: "/site/careers", label: "Careers", icon: Briefcase },
  { to: "/site/contact", label: "Feedback", icon: MessageSquare },
];

function CustomerNav() {
  const { customer, loading, logout } = useCustomerAuth();
  const nav = useNavigate();
  if (loading) return null;

  if (!customer) {
    return (
      <div className="flex items-center gap-1.5">
        <Link
          to="/site/login"
          data-testid="header-customer-login"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-emerald-200/30 text-white hover:bg-emerald-900">
          <LogIn size={13} /> Sign in
        </Link>
        <Link
          to="/site/register"
          data-testid="header-customer-signup"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-amber-500 text-emerald-950 font-semibold hover:bg-amber-400">
          <UserPlus size={13} /> Create account
        </Link>
      </div>
    );
  }

  const initial = customer.full_name?.[0]?.toUpperCase() || "A";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="header-customer-menu"
          variant="ghost"
          className="text-white hover:bg-emerald-900 gap-2 h-9 px-2.5">
          <div className="h-7 w-7 rounded-full bg-amber-500 text-emerald-950 flex items-center justify-center font-bold text-sm">
            {initial}
          </div>
          <span className="hidden sm:inline text-sm font-medium">{customer.full_name.split(" ")[0]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">
          <div className="font-semibold">{customer.full_name}</div>
          <div className="text-gray-500 font-normal">{customer.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav("/site/account")} data-testid="menu-account">
          <User size={13} className="mr-2" /> My account
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/site/account?tab=orders")}>
          <Package size={13} className="mr-2" /> My orders
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/site/account?tab=applications")}>
          <Briefcase size={13} className="mr-2" /> My applications
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => { await logout(); nav("/site"); }}
          data-testid="menu-logout"
          className="text-red-600 focus:text-red-700">
          <LogOut size={13} className="mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PublicLayoutInner() {
  const loc = useLocation();
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/public/tenants/abrah").then(({ data }) => setBrand(data)).catch(() => {});
  }, []);

  const brandName = (brand?.name || "Abrah Supermarket").replace(/ Ltd\.?$/, "");
  return (
    <div className="min-h-screen bg-[#F4F5F1] flex flex-col">
      <header className="bg-[#022C22] text-white border-b border-emerald-900/50 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4">
          <Link to="/site" className="flex items-center gap-2.5 shrink-0">
            <div className={`h-9 w-9 rounded-md flex items-center justify-center font-display font-bold overflow-hidden shrink-0 ${
              brand?.logo_file_id || /abrah/i.test(brandName) ? "bg-white" : "bg-amber-500 text-[#022C22]"
            }`}>
              {brand?.logo_file_id ? (
                <img src={publicTenantLogoUrl(brand.logo_file_id)} alt={`${brandName} logo`}
                  className="h-full w-full object-contain" data-testid="public-header-logo" />
              ) : /abrah/i.test(brandName) ? (
                <img src="/abrah-logo.jpeg" alt="Abrah Supermarket logo"
                  className="h-full w-full object-contain" data-testid="public-header-default-logo" />
              ) : brandName.slice(0, 1).toUpperCase()}
            </div>
            <div className="hidden sm:block">
              <div className="font-display font-bold text-base leading-tight" data-testid="public-header-name">{brandName}</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-200/70">Uganda · Trusted Retail</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-4">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to}
                  className={`px-3 py-2 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                    active ? "bg-emerald-800 text-white" : "text-emerald-100 hover:bg-emerald-900"
                  }`}>
                  <Icon size={14} />{n.label}
                </Link>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <CustomerNav />
          </div>
        </div>
        {/* Mobile bottom-line nav */}
        <nav className="md:hidden border-t border-emerald-900/60 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-0.5 px-2 py-1.5 min-w-max">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to}
                  className={`px-2.5 py-1.5 text-[11px] rounded-md flex items-center gap-1 ${
                    active ? "bg-emerald-800 text-white" : "text-emerald-100"
                  }`}>
                  <Icon size={12} />{n.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="bg-emerald-950 text-emerald-100/70 text-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row gap-3 justify-between">
          <div>© {new Date().getFullYear()} Abrah Supermarket Ltd · Powered by AEOIBCMS</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/site/careers" className="hover:text-white">Careers</Link>
            <Link to="/site/contact" className="hover:text-white">Customer feedback</Link>
            <Link to="/site/account" className="hover:text-white">My account</Link>
            <Link to="/login" className="hover:text-white opacity-70">Staff sign-in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function PublicLayout() {
  return (
    <CustomerAuthProvider>
      <PublicLayoutInner />
    </CustomerAuthProvider>
  );
}
